<script setup>
import { ref, onMounted, defineExpose } from "vue";

const props = defineProps({
  article_type: { type: Object, required: true },
  size: { type: String, default: "md" }
});

// Emits to Modal.vue → ModalManager
const emit = defineEmits(["close"]);

// ============================================================
// STATE
// ============================================================
const loading = ref(true);
const error   = ref(null);

const groups = ref([]);
const assignedGroupIds = ref([]);   // <─ exposed for detectValidation()
const editorEl = ref(null);

// Expose data used by ModalManager.detectValidation()
defineExpose({
  assignedGroupIds
});

// ============================================================
// LOAD GROUP LIST + ASSIGNMENTS
// ============================================================
async function load() {
  loading.value = true;
  error.value = null;

  try {
    const apiKey = localStorage.getItem("api_key");

    // 1. All groups
    const resGroups = await fetch(`/api/article-groups`, {
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { "x-api-key": apiKey } : {})
      }
    });
    groups.value = await resGroups.json();

    // 2. Assigned groups for this type
    const resAssigned = await fetch(`/api/article-types/${props.article_type.id}/groups`, {
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { "x-api-key": apiKey } : {})
      }
    });
    const assigned = await resAssigned.json();

    assignedGroupIds.value = assigned.map(r => r.group_id);

  } catch (err) {
    console.error("❌ TypeGroupEditor load error:", err);
    error.value = "Failed to load group data.";
  }

  loading.value = false;
}

// ============================================================
// ASSIGN / UNASSIGN
// ============================================================
async function assignGroup(groupId) {
  const apiKey = localStorage.getItem("api_key");

  await fetch(`/api/article-types/${props.article_type.id}/groups`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(apiKey ? { "x-api-key": apiKey } : {})
    },
    body: JSON.stringify({ group_id: groupId })
  });

  assignedGroupIds.value.push(groupId);
  flashSaved();
}

async function unassignGroup(groupId) {
  const apiKey = localStorage.getItem("api_key");

  await fetch(`/api/article-types/${props.article_type.id}/groups/${groupId}`, {
    method: "DELETE",
    headers: {
      ...(apiKey ? { "x-api-key": apiKey } : {})
    }
  });

  assignedGroupIds.value = assignedGroupIds.value.filter(id => id !== groupId);
  flashSaved();
}

// ============================================================
// CHECKBOX HANDLER
// ============================================================
async function toggleGroup(groupId, event) {
  if (event.target.checked) {
    await assignGroup(groupId);
  } else {
    await unassignGroup(groupId);
  }
}

// ============================================================
// DONE BUTTON → Close Modal
// ============================================================
function cancel() {
  emit("close", assignedGroupIds.value.length > 0);
}

// ============================================================
// FLASH EFFECT
// ============================================================
function flashSaved() {
  const el = editorEl.value;
  if (!el) return;

  el.classList.add("nz-saved");
  setTimeout(() => el.classList.remove("nz-saved"), 1500);
}

// ============================================================
// MOUNT
// ============================================================
onMounted(load);
</script>


<template>
  <div class="group-editor" ref="editorEl">

    <div v-if="loading" class="nz-loading">Loading…</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <div v-else class="group-list">

      <label
        v-for="g in groups"
        :key="g.id"
        class="group-row"
        :class="{ selected: assignedGroupIds.includes(g.id) }"
        :data-nz-valid="assignedGroupIds.includes(g.id)"
      >
        <input
          type="checkbox"
          :checked="assignedGroupIds.includes(g.id)"
          @change="toggleGroup(g.id, $event)"
        />
        {{ g.label }}
      </label>

    </div>

    <div class="nz-modal-footer">
      <button class="btn btn-primary" @click="cancel">Done</button>
    </div>

  </div>
</template>


<style scoped>
.group-row {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 6px 0;
  cursor: pointer;
}

.error-msg {
  padding: 14px;
  color: red;
}

.btn-primary {
  margin-right: 16px;
}

.group-editor {
  padding: 0 0 0 6px;
}

/* Flash checkmark effect */
.group-editor.nz-saved {
  background: transparent url('https://comfortpages.com/api/svg/checkmark/2bafbc') no-repeat right bottom;
  background-size: auto 32px;
}
</style>
